import React, { useState } from "react";
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Space,
  Switch,
  Tag,
  Typography
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { createRoom, deleteRoom, updateRoom } from "./api";

/** Add, edit and delete the provider's rooms. `onChanged` reloads the timeline. */
const ModalRoom = ({ open, rooms, loading, onClose, onChanged, can }) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  // null: no form shown, {}: new room, a room: editing it.
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const startEdit = room => {
    setEditing(room);
    form.setFieldsValue({
      name: room.name || "",
      capacity: room.capacity ?? null,
      location: room.location || "",
      description: room.description || "",
      active: room.active ?? true
    });
  };

  const close = () => {
    setEditing(null);
    onClose();
  };

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const body = { ...values, name: values.name.trim() };
    setSaving(true);
    try {
      if (editing?._id) await updateRoom(editing._id, body);
      else await createRoom(body);
      Success(editing?._id ? t("Room updated successfully") : t("Room added successfully"));
      setEditing(null);
      onChanged();
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async room => {
    setDeletingId(room._id);
    try {
      await deleteRoom(room._id);
      Success(t("Room deleted successfully"));
      if (editing?._id === room._id) setEditing(null);
      onChanged();
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setDeletingId(null);
    }
  };

  const columns = [
    {
      title: t("Room"),
      dataIndex: "name",
      key: "name",
      mobile: "title",
      render: (name, room) => (
        <Space size={4} wrap>
          <Typography.Text strong>{name}</Typography.Text>
          {!room.active && <Tag>{t("Inactive")}</Tag>}
        </Space>
      )
    },
    {
      title: t("Capacity"),
      dataIndex: "capacity",
      key: "capacity",
      align: "end",
      render: value => value ?? "–"
    },
    {
      title: t("Location"),
      dataIndex: "location",
      key: "location",
      mobile: "subtitle",
      render: value => value || "–"
    },
    {
      title: t("Description"),
      dataIndex: "description",
      key: "description",
      render: value => value || "–"
    },
    {
      title: t("Actions"),
      key: "actions",
      mobile: "actions",
      render: room => (
        <Space size={4} wrap>
          {can("Update") && (
            <Button type="text" icon={<EditOutlined />} onClick={() => startEdit(room)}>
              {t("Edit")}
            </Button>
          )}
          {can("Delete") && (
            <Popconfirm
              title={t("Delete this room?")}
              description={t("All bookings of this room are deleted too.")}
              okText={t("Delete")}
              cancelText={t("Cancel")}
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(room)}
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                loading={deletingId === room._id}
              >
                {t("Delete")}
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  const footer = editing
    ? [
        <Button key="cancel" onClick={() => setEditing(null)}>
          {t("Cancel")}
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
          {editing._id ? t("Save") : t("Add room")}
        </Button>
      ]
    : [
        <Button key="close" onClick={close}>
          {t("Close")}
        </Button>,
        can("Create") && (
          <Button
            key="add"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => startEdit({ active: true })}
          >
            {t("Add room")}
          </Button>
        )
      ].filter(Boolean);

  return (
    <Modal
      centered
      width={860}
      open={open}
      forceRender
      title={editing ? (editing._id ? t("Edit room") : t("Add room")) : t("Manage rooms")}
      onCancel={close}
      footer={footer}
    >
      {editing ? (
        <Form form={form} layout="vertical" requiredMark={false}>
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label={t("Room name")}
                rules={[
                  { required: true, whitespace: true, message: t("Please enter a room name") },
                  { max: 120 }
                ]}
              >
                <Input placeholder={t("e.g. Meeting room 1")} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="capacity" label={t("Capacity (people)")}>
                <InputNumber min={0} precision={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="location" label={t("Location")}>
                <Input placeholder={t("e.g. 2nd floor, building B")} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="active"
                label={t("Can be booked")}
                valuePropName="checked"
                tooltip={t("Inactive rooms keep their bookings but take no new ones.")}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="description" label={t("Description")}>
                <Input.TextArea rows={3} maxLength={2000} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      ) : (
        <ResponsiveTable
          rowKey="_id"
          dataSource={rooms}
          columns={columns}
          loading={loading}
          pagination={false}
          locale={{ emptyText: t("No rooms yet") }}
        />
      )}
    </Modal>
  );
};

export default ModalRoom;
